import { gql } from '@apollo/client';
import { FULL_CHANNEL } from './pages/queries.tsx';
import {
    BASE_DOCUMENT,
    BASE_FIELD,
    EXTRACTION_SEARCH_RESULTS,
    PRIMITIVE_FIELD,
    SELECT_FIELD_FRAGMENT,
} from '../../document/queries.ts';

const FULL_INVOICE = gql`
    ${SELECT_FIELD_FRAGMENT}

    fragment FullInvoice on Invoice {
        ${BASE_DOCUMENT}

        invoiceNumber {
            ${PRIMITIVE_FIELD}
        }

        orderNumber {
            ${PRIMITIVE_FIELD}
        }

        contractNumber {
            ${PRIMITIVE_FIELD}
        }

        deliveryNoteNumber {
            ${PRIMITIVE_FIELD}
        }

        invoiceDate {
            ${PRIMITIVE_FIELD}
        }

        invoiceDueDate {
            ${PRIMITIVE_FIELD}
        }

        deliveryDate {
            ${PRIMITIVE_FIELD}
        }

        orderDate {
            ${PRIMITIVE_FIELD}
        }

        recipientCustomerNumber {
            ${PRIMITIVE_FIELD}
        }

        recipientAddress {
            addressId
            name
            postcode
            city
            countrySubdivision
            country
            phone
            email
            companyName
            streetAndNr
            streetName
            streetNumber
            ${BASE_FIELD}
        }

        recipientContact {
            name
            contactId
            phone
            email
            ${BASE_FIELD}
        }

        senderSupplier {
            name
            customerNumber
            customerType
            misc
            contactPerson
            ${BASE_FIELD}

            address {
                addressId
                name
                postcode
                city
                country
                phone
                email
                companyName
                streetAndNr
                misc
                bbox
                pageIndex
                predictionConfidence
            }
            internalNote
        }

        senderVatId {
            ${PRIMITIVE_FIELD}
        }

        senderTaxNumber {
            ${PRIMITIVE_FIELD}
        }

        senderAddress {
            addressId
            name
            postcode
            city
            countrySubdivision
            country
            phone
            email
            companyName
            streetAndNr
            streetName
            streetNumber
            ${BASE_FIELD}
        }

        senderContact {
            name
            bbox
            pageIndex
            contactId
            phone
            email
            ${BASE_FIELD}
        }

        paymentBank {
            ${PRIMITIVE_FIELD}
        }

        paymentBic {
            ${PRIMITIVE_FIELD}
        }

        paymentIban {
            ${PRIMITIVE_FIELD}
        }

        paymentMethod {
            ${PRIMITIVE_FIELD}
        }

        paymentReference {
            ${PRIMITIVE_FIELD}
        }

        paymentSwift {
            ${PRIMITIVE_FIELD}
        }

        paymentTermsDiscount {
            ${PRIMITIVE_FIELD}
        }

        paymentTermsDueDays {
            ${PRIMITIVE_FIELD}
        }

        paymentTermsType {
            ${PRIMITIVE_FIELD}
        }

        paymentTerms {
            ${PRIMITIVE_FIELD}
        }

        currency {
            ...FullSelectField
        }

        totalAmount {
            ${PRIMITIVE_FIELD}
        }

        totalDue {
            ${PRIMITIVE_FIELD}
        }

        taxRate {
            ${PRIMITIVE_FIELD}
        }

        taxAmount {
            ${PRIMITIVE_FIELD}
        }

        documentType {
            ${PRIMITIVE_FIELD}
        }

        netAmount {
            ${PRIMITIVE_FIELD}
        }

        infoText {
            ${PRIMITIVE_FIELD}
        }

        lineItems {
            id
            tableId
            parentLineItemId

            isMatchingIgnored
            ${BASE_FIELD}

            pos {
                ${PRIMITIVE_FIELD}
            }
            articleNumber {
                ${PRIMITIVE_FIELD}
            }
            articleNumber2 {
                ${PRIMITIVE_FIELD}
            }
            articleNumber3 {
                ${PRIMITIVE_FIELD}
            }
            articlePartition {
                ${PRIMITIVE_FIELD}
            }
            articlePartitionSpecificNumber {
                ${PRIMITIVE_FIELD}
            }
            fulfillmentDate {
                ${PRIMITIVE_FIELD}
            }
            orderDate {
                ${PRIMITIVE_FIELD}
            }
            articleDescription {
                ${PRIMITIVE_FIELD}
            }
            articleDescriptionMasterData {
                ${PRIMITIVE_FIELD}
            }
            ${EXTRACTION_SEARCH_RESULTS}
            grossPrice {
                ${PRIMITIVE_FIELD}
            }
            unitPrice {
                ${PRIMITIVE_FIELD}
            }
            totalPrice {
                ${PRIMITIVE_FIELD}
            }
            unit {
                ...FullSelectField
            }
            quantity {
                ${PRIMITIVE_FIELD}
            }
            taxRate {
                ${PRIMITIVE_FIELD}
            }
            taxAmount {
                ${PRIMITIVE_FIELD}
            }
            orderNumber {
                ${PRIMITIVE_FIELD}
            }
            priceUnit {
                ...FullSelectField
            }
            dynamicSchemaSerialized
        }
        dynamicSchemaSerialized
    }
`;

const FULL_RECORD = gql`
    ${FULL_INVOICE}
    ${FULL_CHANNEL}
    fragment FullRecord on InvoiceProcessingRecord {
        id
        createdAt
        deletedAt
        deletedBy {
            email
            firstName
            lastName
        }
        deletedReason
        emailCount

        sender
        subject

        status

        canFinishAssistance
        canFinishHeaderAssistance
        canFinishLineItemsAssistance

        lockedBy {
            email
        }

        putOnHoldReason
        putOnHoldBy {
            email
            firstName
            lastName
        }

        assistedBy {
            email
            firstName
            lastName
        }

        assistedAt

        createdBy {
            email
            firstName
            lastName
        }

        assignedTo {
            id
            firstName
            lastName
            email
        }

        createdSource

        stepRun {
            id
            stepName
            manualOnly
            executionStatus
            error
            context
        }

        invoiceprocessingsteprunSet {
            id
            createdAt
            updatedAt
            stepName
            manualOnly
            executionStatus
            context
            error
        }

        channel {
            ...FullChannel
        }

        isDiscarded
        isTestDocument
        documentType

        discardRecord {
            reason
            user {
                id
                firstName
                lastName
            }
            createdAt
            updatedAt
        }

        deliveryRecord {
            deliveryMethod
            deliveryStatus
            externalReference
            externalMessage
            externalStatus
            externalUrl
        }

        sourceFile {
            ... on Email {
                sender {
                    email
                    name
                }
                receivers {
                    email
                    name
                }
                subject
                messageId
                language
                receivedAt

                text
                html
                attachments {
                    cid
                    contentDisposition
                    name
                    type
                    url
                    sourcePartId
                }

                senderClientId
                prediction
                predictionConfidence
                sourceUrl

                predictionConfidences
            }

            ... on File {
                name
                type
                url
            }
        }

        sourceParts {
            id
            fileName
            mimeType
            partType
            fileUrl
            classificationLabel
            classificationConfidence
            recordId
            classificationCorrectedBy
            classificationCorrectedAt
        }

        inputFile {
            ... on File {
                name
                type
                sourceType
                url
            }
        }

        outputFile {
            ... on Export {
                files {
                    name
                    url
                }
            }
        }

        invoiceUnsaved {
            ...FullInvoice
        }

        labelingStatus

        masterdataVersionsExtracted
        eInvoice
        isMatchingEnabled
    }
`;

export const GET_OVERVIEW_DATA = gql`
    query {
        invoiceProcessingChannels {
            id
            name
        }
    }
`;

export const GET_CHANNELS_OVERVIEW_DATA = gql`
    query {
        invoiceProcessingChannels {
            id
            name
            emailAddress
            team {
                id
                users {
                    email
                    firstName
                    lastName
                }
            }
        }
    }
`;

export const GET_TABLE_OVERVIEW_DATA = gql`
    query ($filters: [TableFilter], $orderBy: String, $offset: Int, $first: Int) {
        filteredInvoiceProcessingRecordsList(filters: $filters, orderBy: $orderBy, offset: $offset, limit: $first) {
            totalCount
            records {
                id
                sender
                status
                createdAt
                finishedAt
                assistedAt
                assistedBy {
                    id
                    firstName
                    lastName
                    email
                }
                createdBy {
                    email
                    firstName
                    lastName
                }
                assignedTo {
                    id
                    firstName
                    lastName
                    email
                }
                createdSource
                deletedAt
                deletedBy {
                    id
                    firstName
                    lastName
                    email
                }
                deletedReason
                stepRun {
                    id
                    manualOnly
                    executionStatus
                    error
                    context
                }

                lockedBy {
                    email
                }

                channel {
                    id
                    name

                    extractorConfig {
                        validationChecks
                    }
                }
                isDiscarded
                deliveryRecord {
                    deliveryMethod
                    deliveryStatus
                    externalReference
                    externalMessage
                    externalStatus
                }
                discardRecord {
                    reason
                    user {
                        id
                        firstName
                        lastName
                        email
                    }
                    createdAt
                    updatedAt
                }
                invoiceUnsaved {
                    orderNumber {
                        value
                        predictionConfidence
                        confidenceExplanation {
                            translationKey
                            explanationDetails
                        }
                    }
                }
                isTestDocument
                canProcessAutomatically
                title
                subtitle
                attachmentsCount
                sourcePart {
                    sender {
                        email
                        name
                    }
                }
                emailCount
                putOnHoldReason
                putOnHoldBy {
                    firstName
                    lastName
                }
            }
        }
    }
`;

export const GET_ASSISTANCE_OVERVIEW_DATA = gql`
    query ($recordId: UUID!, $filters: [TableFilter]) {
        invoiceProcessingRecordAssistancePagination(recordId: $recordId, filters: $filters) {
            filteredCount
            position
            previousRecordId
            nextRecordId
        }
    }
`;

export const GET_ASSISTANCE_DATA = gql`
    ${FULL_RECORD}
    ${FULL_INVOICE}
    query ($id: UUID!) {
        invoiceProcessingRecord(id: $id) {
            ...FullRecord

            invoiceExtracted {
                ...FullInvoice
            }

            dusDocument {
                pagesCount
                processedFileUrl
            }

            dusEmail {
                pagesCount
                processedFileUrl
            }
        }
    }
`;

export const UPDATE_PROCESSING_RECORD = gql`
    mutation ($recordId: UUID!, $fieldName: String, $itemIndex: Int, $action: String!, $payload: GenericScalar) {
        invoiceUpdate(
            recordId: $recordId
            fieldName: $fieldName
            itemIndex: $itemIndex
            action: $action
            payload: $payload
        ) {
            record {
                canFinishAssistance
                canFinishHeaderAssistance
                canFinishLineItemsAssistance
                stepRun {
                    id
                    executionStatus
                    manualOnly
                    stepName
                }
            }

            patch {
                op
                path
                from
                value
            }
        }
    }
`;

export const RESELECT_PROCESSING_RECORD = gql`
    mutation (
        $recordId: UUID!
        $fieldName: String
        $bbox: [[Float]]!
        $pageIndex: Int!
        $itemIndex: Int
        $isEmailOcr: Boolean
    ) {
        invoiceReselect(
            recordId: $recordId
            fieldName: $fieldName
            bbox: $bbox
            pageIndex: $pageIndex
            itemIndex: $itemIndex
            isEmailOcr: $isEmailOcr
        ) {
            record {
                canFinishAssistance
                canFinishHeaderAssistance
                canFinishLineItemsAssistance
            }

            patch {
                op
                path
                from
                value
            }
        }
    }
`;

export const RETRY_STEP = gql`
    mutation ($recordId: UUID!, $stepRunId: UUID!) {
        invoiceRetryStep(recordId: $recordId, stepRunId: $stepRunId) {
            success
        }
    }
`;

export const REOPEN_FOR_ASSISTANCE = gql`
    mutation ($recordId: UUID!, $reason: String) {
        invoiceReopenForAssistance(recordId: $recordId, reason: $reason) {
            success
        }
    }
`;

export const UPLOAD_FILE = gql`
    mutation ($channelId: UUID!, $files: [Upload]!, $batchId: String, $isTestingDocument: Boolean!) {
        invoiceUploadFile(
            input: { channelId: $channelId, files: $files, batchId: $batchId, isTestingDocument: $isTestingDocument }
        ) {
            records {
                id
            }
            success
        }
    }
`;

export const RE_UPLOAD_FILE = gql`
    mutation ($channelId: UUID!, $files: [Upload]!, $recordId: String!, $isTestingDocument: Boolean!) {
        invoiceUploadFile(
            input: { channelId: $channelId, files: $files, recordId: $recordId, isTestingDocument: $isTestingDocument }
        ) {
            records {
                id
            }
            success
        }
    }
`;

export const SEND_TO_LABELING = gql`
    mutation ($recordId: UUID!) {
        invoiceSendToLabeling(recordId: $recordId) {
            success
        }
    }
`;

export const GET_NEXT_ASSISTANCE_RECORD = gql`
    query ($filters: [TableFilter], $channelId: UUID, $orderBy: String) {
        nextInvoiceProcessingRecord(filters: $filters, channelId: $channelId, orderBy: $orderBy) {
            id
        }
    }
`;

export const CREATE_CHANNEL = gql`
    mutation ($name: String!, $fromExistingChannelId: UUID) {
        createInvoiceProcessingChannel(name: $name, fromExistingChannelId: $fromExistingChannelId) {
            channel {
                id
            }
            success
        }
    }
`;

export const GET_ALL_CHANNELS = gql`
    ${FULL_CHANNEL}
    query {
        invoiceProcessingChannels {
            ...FullChannel
        }
    }
`;

export const INVOICE_EXTRACT_TEXT = gql`
    mutation InvoiceExtractText($recordId: UUID!, $pageIndex: Int!, $bbox: [[Float]]!, $isEmailOcr: Boolean = false) {
        invoiceExtractText(recordId: $recordId, pageIndex: $pageIndex, bbox: $bbox, isEmailOcr: $isEmailOcr) {
            bbox
            text
        }
    }
`;

export const GET_RELATED_DOCUMENT = gql`
    query ($id: UUID!) {
        invoiceProcessingRecord(id: $id) {
            id
            subject
            createdAt
            finishedAt
            assistedAt
            assistedBy {
                id
                firstName
                lastName
                email
            }
            status
        }
    }
`;
